/* Cart cross-sell recommendations. */
/* global theme */

(function shopifyCartCrossSells() {
  const ROOT_SELECTOR = '[data-cart-cross-sells]';
  const SLIDER_SELECTOR = '[data-cart-cross-sells-slider]';
  const SKELETON_SELECTOR = '[data-cart-cross-sells-skeleton]';
  const CARD_SELECTOR = '[data-upsell-holder]';
  const COLLAPSIBLE_TRIGGER_SELECTOR = '[data-collapsible-trigger]';
  const COLLAPSIBLE_CONTAINER_SELECTOR = '[data-collapsible-container]';
  const COLLAPSIBLE_CONTENT_SELECTOR = '[data-collapsible-content]';
  const EXPANDED_CLASS = 'is-expanded';
  const COLLAPSIBLE_ANIMATION_DELAY = 500;
  const SEED_STORAGE_KEY = 'cartCrossSellsProductId';
  const rootStateMap = new WeakMap();
  const timerMap = new WeakMap();
  const recommendationsCache = new Map();

  function getRootState(root) {
    if (!rootStateMap.has(root)) {
      rootStateMap.set(root, {
        collapsibleInitialized: false,
        flkty: null,
        requestId: 0,
      });
    }

    return rootStateMap.get(root);
  }

  function setElementHidden(element, isHidden) {
    const node = element;
    node.hidden = isHidden;
  }

  function setElementHeight(element, value) {
    const node = element;
    node.style.height = value;
  }

  function clearElementTimer(element) {
    const timerId = timerMap.get(element);
    if (timerId) clearTimeout(timerId);
  }

  function setElementTimer(element, timerId) {
    timerMap.set(element, timerId);
  }

  function getFlickityLib() {
    return (window.themeVendor && window.themeVendor.Flickity) || window.Flickity;
  }

  function destroySliderInstance(slider, rootState) {
    const state = rootState;
    const FlickityLib = getFlickityLib();
    const sliderInstance = FlickityLib && typeof FlickityLib.data === 'function' ? FlickityLib.data(slider) : null;

    [sliderInstance, state.flkty].forEach((instance) => {
      if (!instance || typeof instance.destroy !== 'function') return;
      try {
        instance.destroy();
      } catch {
        // A detached slider can already have been destroyed.
      }
    });

    state.flkty = null;
  }

  function normalizeOptionValue(value) {
    return String(value || '')
      .trim()
      .toLowerCase();
  }

  function getCartOptionPreferences(cartItems) {
    return (cartItems || []).reduce(
      (preferences, item) => {
        const colorValue = normalizeOptionValue(item.variant_options && item.variant_options[0]);
        const sizeValue = normalizeOptionValue(item.variant_options && item.variant_options[1]);

        if (colorValue) preferences.colorValues.add(colorValue);
        if (sizeValue) preferences.sizeValues.add(sizeValue);

        return preferences;
      },
      { colorValues: new Set(), sizeValues: new Set() },
    );
  }

  function saveRecommendationSeed(item) {
    const productId = item && item.product_id;
    if (!productId) return;

    try {
      window.sessionStorage.setItem(SEED_STORAGE_KEY, String(productId));
    } catch {
      // Recommendations still work with the cart fallback when storage is unavailable.
    }
  }

  function getSavedRecommendationSeed() {
    try {
      return window.sessionStorage.getItem(SEED_STORAGE_KEY) || '';
    } catch {
      return '';
    }
  }

  function getCartContext() {
    return fetch(`${theme.routes.cart_url}.js`, { credentials: 'same-origin' })
      .then((response) => {
        if (!response.ok) throw new Error('Failed to load cart');
        return response.json();
      })
      .then((cart) => {
        const items = cart.items || [];
        const productIds = items.map((item) => String(item.product_id)).filter(Boolean);
        const savedSeed = getSavedRecommendationSeed();
        const seedProductId = productIds.includes(savedSeed) ? savedSeed : productIds[0] || '';

        return {
          productIds,
          optionPreferences: getCartOptionPreferences(items),
          seedProductId,
        };
      });
  }

  function getRecommendations(productId, limit) {
    const cacheKey = `${productId}:${limit}`;
    if (recommendationsCache.has(cacheKey)) return recommendationsCache.get(cacheKey);

    const params = new URLSearchParams({
      product_id: productId,
      intent: 'related',
      limit: String(limit),
    });

    const request = fetch(`${theme.routes.product_recommendations_url}.json?${params.toString()}`, {
      credentials: 'same-origin',
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Shopify recommendations request failed (${response.status})`);
        return response.json();
      })
      .then((result) => (Array.isArray(result.products) ? result.products : []))
      .catch((error) => {
        recommendationsCache.delete(cacheKey);
        throw error;
      });

    recommendationsCache.set(cacheKey, request);
    return request;
  }

  function findBestMatchingVariant(product, optionPreferences) {
    const variants = (product.variants || []).filter((variant) => variant.available);
    const preferences = optionPreferences || { colorValues: new Set(), sizeValues: new Set() };

    return (
      variants.reduce((best, variant) => {
        const colorScore = preferences.colorValues.has(normalizeOptionValue(variant.option1)) ? 2 : 0;
        const sizeScore = preferences.sizeValues.has(normalizeOptionValue(variant.option2)) ? 1 : 0;
        const score = colorScore + sizeScore;

        return !best || score > best.score ? { score, variant } : best;
      }, null)?.variant || null
    );
  }

  function getProductUrl(product, variant, rootUrl) {
    const fallbackUrl = `${rootUrl.replace(/\/$/, '')}/products/${product.handle}`;
    const url = new URL(product.url || fallbackUrl, window.location.origin);
    if (variant && variant.id) url.searchParams.set('variant', String(variant.id));
    return `${url.pathname}${url.search}${url.hash}`;
  }

  function getImageUrl(product, variant) {
    const image =
      variant.featured_image ||
      product.featured_image ||
      product.image ||
      (product.images && product.images[0]) ||
      (product.media && product.media[0] && product.media[0].preview_image);
    const source = typeof image === 'string' ? image : image && (image.src || image.url);
    if (!source) return '';

    try {
      const url = new URL(source, window.location.origin);
      url.searchParams.set('width', '600');
      url.searchParams.set('height', '600');
      url.searchParams.set('crop', 'center');
      return url.toString();
    } catch {
      return source;
    }
  }

  function formatPrice(rawPrice) {
    const cents = Number(rawPrice);
    const money = window.themeVendor && window.themeVendor.themeCurrency;
    return Number.isFinite(cents) && money ? money.formatMoney(cents, theme.moneyFormat) : '';
  }

  function createCard(product, cartProductIds, optionPreferences, rootUrl, quickViewLabel, cardTemplate) {
    const productId = String(product.id || '');
    const variant = findBestMatchingVariant(product, optionPreferences);
    if (!product.handle || !variant || cartProductIds.includes(productId)) return null;

    const card = cardTemplate.content.firstElementChild.cloneNode(true);
    const title = String(product.title || '');
    const productUrl = getProductUrl(product, variant, rootUrl);
    const imageUrl = getImageUrl(product, variant);
    const hasCompareAt = Number(variant.compare_at_price) > Number(variant.price);
    const imageLink = card.querySelector('[data-cart-cross-sells-image-link]');
    const image = card.querySelector('[data-cart-cross-sells-image]');
    const quickViewLink = card.querySelector('[data-cart-cross-sells-quick-view-link]');
    const quickViewText = card.querySelector('[data-cart-cross-sells-quick-view-text]');
    const titleLink = card.querySelector('[data-cart-cross-sells-title-link]');
    const price = card.querySelector('[data-cart-cross-sells-price]');
    const salePrice = card.querySelector('[data-cart-cross-sells-price-new]');
    const comparePrice = card.querySelector('[data-cart-cross-sells-price-compare]');

    if (imageLink) {
      imageLink.href = productUrl;
      imageLink.setAttribute('aria-label', title);
    }
    if (image) {
      image.alt = title;
      if (imageUrl) image.src = imageUrl;
      else image.remove();
    }
    if (quickViewLink) {
      quickViewLink.href = productUrl;
      quickViewLink.setAttribute('aria-label', `${quickViewLabel}: ${title}`);
      quickViewLink.setAttribute('data-handle', product.handle);
    }
    if (quickViewText) quickViewText.textContent = quickViewLabel;
    if (titleLink) {
      titleLink.href = productUrl;
      titleLink.textContent = title;
    }
    if (price) {
      if (hasCompareAt) {
        salePrice.hidden = false;
        salePrice.textContent = formatPrice(variant.price);
        comparePrice.hidden = false;
        comparePrice.textContent = formatPrice(variant.compare_at_price);
      } else {
        price.textContent = formatPrice(variant.price);
      }
    }

    return card;
  }

  function getCollapsibleElements(root) {
    const trigger = root.querySelector(COLLAPSIBLE_TRIGGER_SELECTOR);
    const container = root.querySelector(COLLAPSIBLE_CONTAINER_SELECTOR);
    const content = root.querySelector(COLLAPSIBLE_CONTENT_SELECTOR);
    return trigger && container && content ? { trigger, container, content } : null;
  }

  function setExpandedState(elements, isExpanded) {
    elements.trigger.classList.toggle(EXPANDED_CLASS, isExpanded);
    elements.container.classList.toggle(EXPANDED_CLASS, isExpanded);
    elements.trigger.setAttribute('aria-expanded', String(isExpanded));
    elements.container.setAttribute('aria-expanded', String(isExpanded));
  }

  function initCollapsible(root) {
    const rootState = getRootState(root);
    const elements = getCollapsibleElements(root);
    if (!elements) return;

    setExpandedState(elements, true);
    setElementHeight(elements.container, 'auto');
    if (rootState.collapsibleInitialized) return;

    elements.trigger.addEventListener('click', (event) => {
      event.preventDefault();
      const isExpanded = elements.trigger.classList.contains(EXPANDED_CLASS);
      const contentHeight = elements.content.offsetHeight;
      setElementHeight(elements.container, `${contentHeight}px`);

      requestAnimationFrame(() => {
        setExpandedState(elements, !isExpanded);
        setElementHeight(elements.container, isExpanded ? '0px' : `${elements.content.offsetHeight}px`);
      });

      clearElementTimer(elements.container);
      setElementTimer(
        elements.container,
        setTimeout(() => setElementHeight(elements.container, isExpanded ? '' : 'auto'), COLLAPSIBLE_ANIMATION_DELAY),
      );
    });
    rootState.collapsibleInitialized = true;
  }

  function bindQuickView(root) {
    const QuickViewPopup = window.themeQuickViewPopup;
    if (typeof QuickViewPopup !== 'function') return null;

    return new QuickViewPopup(root.closest('[data-cart-drawer]') || document);
  }

  function initSlider(root, slider) {
    const rootState = getRootState(root);
    const cards = slider.querySelectorAll(CARD_SELECTOR);
    setElementHidden(slider, false);
    setElementHidden(root, cards.length === 0);
    if (!cards.length) return;

    initCollapsible(root);
    bindQuickView(root);

    const FlickityLib = getFlickityLib();
    if (cards.length < 2 || !FlickityLib) return;
    rootState.flkty = new FlickityLib(slider, {
      prevNextButtons: true,
      pageDots: false,
      cellAlign: 'left',
      wrapAround: false,
      groupCells: true,
      contain: true,
    });
  }

  function setLoadingState(root, isLoading) {
    const slider = root.querySelector(SLIDER_SELECTOR);
    const skeleton = root.querySelector(SKELETON_SELECTOR);
    const hasCards = slider && slider.querySelector(CARD_SELECTOR);
    if (skeleton) setElementHidden(skeleton, !isLoading || Boolean(hasCards));
    if (slider) setElementHidden(slider, isLoading && !hasCards);
    if (isLoading) setElementHidden(root, false);
  }

  function loadRecommendations(root) {
    const rootState = getRootState(root);
    const slider = root.querySelector(SLIDER_SELECTOR);
    if (!slider) return;

    const requestId = rootState.requestId + 1;
    rootState.requestId = requestId;
    const isStale = () => rootState.requestId !== requestId || !root.isConnected;
    setLoadingState(root, true);

    const cardTemplate = root.querySelector('[data-cart-cross-sells-card-template]');
    const limit = Number(root.dataset.limit || 8);

    getCartContext()
      .then(({ productIds, optionPreferences, seedProductId }) => {
        if (!seedProductId) throw new Error('No cart product available for recommendations');
        return getRecommendations(seedProductId, limit).then((products) => ({
          products,
          productIds,
          optionPreferences,
        }));
      })
      .then((result) => {
        if (isStale() || !result || !result.products) return;
        const cards = result.products
          .map((product) =>
            createCard(
              product,
              result.productIds,
              result.optionPreferences,
              root.dataset.rootUrl || '/',
              root.dataset.quickViewLabel || 'Quick shop',
              cardTemplate,
            ),
          )
          .filter(Boolean);
        if (!cards.length) throw new Error('No Shopify recommendations available');

        const fragment = document.createDocumentFragment();
        cards.forEach((card) => fragment.appendChild(card));
        destroySliderInstance(slider, rootState);
        slider.replaceChildren(fragment);
        setLoadingState(root, false);
        initSlider(root, slider);
      })
      .catch(() => {
        if (isStale()) return;
        setLoadingState(root, false);
        setElementHidden(root, true);
      });
  }

  function refreshAll() {
    if (document.querySelector('[data-cart-confirmation-popup]')?.open) return;

    document.querySelectorAll(ROOT_SELECTOR).forEach(loadRecommendations);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', refreshAll);
  else refreshAll();

  document.addEventListener('theme:cart:added', (event) => {
    saveRecommendationSeed(event.detail && event.detail.item);
  });
  document.addEventListener('theme:cart:loaded', refreshAll);
  document.addEventListener('theme:cart:update', refreshAll);
  document.addEventListener('theme:cart:open', refreshAll);
})();
